import { useEffect, useState, useMemo } from 'react'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'

import { FilterOptionsState } from '@mui/material'
import { SelectData } from './select.model'

type Props = {
	id: string
	label: string
	data?: SelectData[]
	multiple?: boolean
	disabled?: boolean
	defaultValue?: SelectData | SelectData[]
	freeSolo?: true | undefined
	allOption?: string
	noOption?: string
	noneOption?: string
	placeholder?: string
	onChange?: (id: string, values: SelectData | SelectData[]) => void
}

const SELECT_ALL_OPTION_ID = '__select_all_option__'

const Select = ({
	id,
	label,
	data = [],
	multiple = false,
	disabled = false,
	onChange,
	defaultValue,
	freeSolo,
	allOption,
	noOption,
	noneOption,
	placeholder,
}: Props) => {
	const [value, setValue] = useState<SelectData>({ label: '', value: '' })
	const [multipleValue, setMultipleValue] = useState<SelectData[]>([])

	const filter = createFilterOptions<SelectData>()

	const areAllOptionsSelected = useMemo(() => {
		if (!multiple || data.length === 0) return false

		return data.every((dataItem) => multipleValue.some((selected) => selected.value === dataItem.value))
	}, [multiple, data, multipleValue])

	const isNoneSelected = useMemo(() => {
		if (!multiple || multipleValue.length === 0) return false
		return multipleValue.length === 1 && multipleValue[0].label === noneOption
	}, [multiple, multipleValue])

	const options = useMemo(() => {
		if (!multiple) return data

		const result = [...data]

		if (allOption && noOption) {
			const selectAllOption: SelectData = {
				label: areAllOptionsSelected ? noOption : allOption,
				value: SELECT_ALL_OPTION_ID,
			}
			result.unshift(selectAllOption)
		}

		return result
	}, [multiple, allOption, noOption, noneOption, data, areAllOptionsSelected, SELECT_ALL_OPTION_ID])

	useEffect(() => {
		if (defaultValue && multiple) {
			setMultipleValue(defaultValue as SelectData[])
		}
		if (defaultValue && !multiple) {
			setValue(defaultValue as SelectData)
		}
	}, [])

	const handleChangeValue = (_: any, v: any) => {
		const mapEntry = (entry: SelectData) => {
			if (entry.newValue === undefined) {
				return entry
			}

			return {
				label: entry.newValue,
				value: entry.newValue,
			}
		}

		if (multiple) {
			if (v && Array.isArray(v)) {
				const selectAllOptionIndex = v.findIndex((item) => item.value === SELECT_ALL_OPTION_ID)
				const selectNoneOptionIndex = v.findIndex((item) => item.label === noneOption)

				if (selectNoneOptionIndex !== -1) {
					v = [v[selectNoneOptionIndex]]
				} else if (selectAllOptionIndex !== -1) {
					v.splice(selectAllOptionIndex, 1)

					if (areAllOptionsSelected) {
						// Deselect All
						v = []
					} else {
						// Select All
						v = [...data]
					}
				}
			}

			v = (v as SelectData[]).map(mapEntry)
			setMultipleValue(v as SelectData[])
		} else {
			setValue(mapEntry(v))
		}

		if (onChange) {
			onChange(id, v)
		}
	}

	const handleFilterOptions = (availableOptions: SelectData[], state: FilterOptionsState<SelectData>): SelectData[] => {
		const filtered = filter(availableOptions, state)

		const { inputValue } = state
		const isExisting = availableOptions.some((option) => inputValue === option.label)
		if (inputValue !== '' && !isExisting) {
			filtered.push({
				newValue: inputValue,
				value: inputValue,
				label: `Add "${inputValue}"`,
			})
		}

		return filtered
	}

	return (
		<Autocomplete
			onChange={handleChangeValue}
			multiple={multiple}
			id={id}
			options={options}
			getOptionLabel={(option: any) => option.label}
			filterOptions={freeSolo ? handleFilterOptions : undefined}
			selectOnFocus={freeSolo}
			clearOnBlur={freeSolo}
			getOptionSelected={(option: any, v: any) => option.value === v.value}
			value={multiple ? multipleValue : value}
			disabled={disabled}
			freeSolo={freeSolo}
			renderInput={(params: any) => (
				<TextField variant="standard" label={label} placeholder={placeholder} {...params} />
			)}
			getOptionDisabled={(option: any) => {
				if (!multiple) return false
				if (option.label === noneOption) return false
				return isNoneSelected
			}}
		/>
	)
}

export default Select
